import { DealStep } from "@/types"
import axios from "../axiosInstance"

// === WORKING ENDPOINTS ===

export const getDealByDealId = async (dealId: string) => {
  try {
    const res = await axios.get(`/deals/${dealId}`)
    const { data } = res
    if (!data.success) {
      throw new Error(data.message || "Failed to fetch deal by deal ID")
    }

    return data.deal
  } catch (error) {
    console.error("Error fetching deal by deal ID:", error)
    throw error
  }
}

export const searchDeals = async (searchTerm: string) => {
  try {
    const res = await axios.get(`/deals/search`, {
      params: { searchTerm },
    })
    const { data } = res
    if (!data.success) {
      throw new Error(data.message || "Failed to search deals")
    }

    return data.deals
  } catch (error) {
    console.error("Error searching deals:", error)
    throw error
  }
}

export const getDealLogs = async (dealId: string) => {
  try {
    const res = await axios.get(`/deals/${dealId}/logs`)
    const { data } = res
    if (!data.success) {
      throw new Error(data.message || "Failed to fetch deal logs")
    }

    return { dealLogs: data.dealLogs, companyLogs: data.companyLogs, hotlistLogs: data.hotlistLogs || [] }
  } catch (error) {
    console.error("Error fetching deal logs:", error)
    throw error
  }
}

export const upsertDealGrants = async (dealId: number) => {
  try {
    const res = await axios.post(`/grants/upsert`, { dealId })
    const { data } = res
    if (!data.success) {
      throw new Error(data.message || "Failed to upsert deal grants")
    }

    return data
  } catch (error) {
    console.error("Error upserting deal grants:", error)
    throw error
  }
}

export const previewDealGrants = async (dealId: number) => {
  try {
    const res = await axios.post(`/grants/preview`, { dealId })
    const { data } = res
    if (!data.success) {
      throw new Error(data.message || "Failed to preview grant extraction")
    }
    return data as {
      success: boolean
      grants: Array<{
        source: string
        companyName: string | null
        grantDescription: string | null
        grantType: "ISO" | "NSO" | "shares"
        vestedShares: number | null
        exercisedShares: number | null
        totalShares: number | null
        pricePerShare: number | null
        grantDate: string | null
        confidence: number
        existingGrantId: number | null
        estimatedOptionsValue: number | null
        tax: number | null
      }>
      fmv: { fmv: number; date: string | null; confidence: number } | null
      fileCount: number
    }
  } catch (error) {
    console.error("Error previewing deal grants:", error)
    throw error
  }
}

export const confirmDealGrants = async (
  dealId: number,
  grants: Array<{
    grantDescription: string
    grantType: "ISO" | "NSO" | "shares"
    vestedShares: number
    pricePerShare: number
    grantDate: string
    existingGrantId: number | null
    tax: number | null
  }>,
  fmv: { fmv: number; date: string | null } | null
) => {
  try {
    const res = await axios.post(`/grants/confirm`, { dealId, grants, fmv })
    const { data } = res
    if (!data.success) {
      throw new Error(data.message || "Failed to confirm grant extraction")
    }
    return data
  } catch (error) {
    console.error("Error confirming deal grants:", error)
    throw error
  }
}

export const confirmStagedGrants = async (dealId: number, grantIds?: number[]) => {
  try {
    const res = await axios.post(`/grants/confirm-staged`, { dealId, grantIds })
    const { data } = res
    if (!data.success) {
      throw new Error(data.message || "Failed to confirm staged grants")
    }
    return data as { success: boolean; confirmed: number }
  } catch (error) {
    console.error("Error confirming staged grants:", error)
    throw error
  }
}

export const getClientDeals = async (clientId?: number | null) => {
  try {
    const res = await axios.get(`/deals/my-deals`, {
      params: clientId ? { clientId } : undefined,
    })
    const { data } = res
    if (!data.success) {
      throw new Error(data.message || "Failed to fetch deals")
    }

    return data.deals
  } catch (error) {
    console.error("Error fetching deals:", error)
    throw error
  }
}

export const getAllFunds = async () => {
  try {
    const res = await axios.get("/deals/funds")
    const { data } = res
    if (!data.success) {
      throw new Error(data.message || "Failed to fetch funds")
    }
    return data.funds
  } catch (error) {
    console.error("Error fetching funds:", error)
    throw error
  }
}

export const getDealPipeline = async (managerId?: number | "all") => {
  try {
    const params: Record<string, string | number> = {}
    if (managerId !== undefined) params.managerId = managerId
    const res = await axios.get("/deals/pipeline", { params })
    return res.data.deals
  } catch (error) {
    console.error("Error fetching deal pipeline:", error)
    throw error
  }
}

export const getDealManagers = async (): Promise<{ id: number; name: string; shortName: string }[]> => {
  try {
    const res = await axios.get("/deals/managers")
    return res.data.managers
  } catch (error) {
    console.error("Error fetching deal managers:", error)
    throw error
  }
}

export const createDeal = async (payload: {
  firstName: string
  lastName: string
  email: string
  companyId: number
  fundId: number
}) => {
  try {
    const res = await axios.post("/deals", payload)
    const { data } = res
    if (!data.success) {
      throw new Error(data.message || "Failed to create deal")
    }
    return { deal: data.deal, employeeMatches: data.employeeMatches || [] }
  } catch (error) {
    console.error("Error creating deal:", error)
    throw error
  }
}

export const confirmLinkedinMatch = async (payload: {
  clientId: number
  linkedinUrl: string
  dealId: number
  firstName: string
  lastName: string
}) => {
  try {
    const res = await axios.post("/deals/confirm-linkedin", payload)
    const { data } = res
    if (!data.success) {
      throw new Error(data.message || "Failed to confirm LinkedIn match")
    }
    return data
  } catch (error) {
    console.error("Error confirming LinkedIn match:", error)
    throw error
  }
}

export const stageOfferInPortal = async (offerId: number, isStagedInPortal: boolean) => {
  try {
    const res = await axios.post(`/deals/offer/stage`, { offerId, isStagedInPortal })
    const { data } = res
    if (!data.success) {
      throw new Error(data.message || "Failed to update offer staging")
    }
    return data.offer
  } catch (error) {
    console.error("Error staging offer in portal:", error)
    throw error
  }
}

export const upsertOfferGrant = async (offerId: number, grantId: number, included: number) => {
  try {
    const res = await axios.post(`/deals/offer/grant`, { offerId, grantId, included })
    const { data } = res
    if (!data.success) {
      throw new Error(data.message || "Failed to upsert offer grant")
    }
    return data.offerGrant
  } catch (error) {
    console.error("Error upserting offer grant:", error)
    throw error
  }
}

export const bulkUpdateDealStage = async (dealIds: number[], stageId: number) => {
  try {
    const res = await axios.post("/deals/bulk-stage", { dealIds, stageId })
    const { data } = res
    if (!data.success) throw new Error(data.message || "Failed to update deal stages")
    return data
  } catch (error) {
    console.error("Error bulk updating deal stages:", error)
    throw error
  }
}

export const upsertDealOffer = async (
  dealId: number,
  offerNumber: number,
  params: Record<string, any>,
  grants: { grantId: number; included: number }[]
) => {
  try {
    const res = await axios.post(`/deals/offer/upsert`, { dealId, offerNumber, params, grants })
    const { data } = res
    if (!data.success) {
      throw new Error(data.message || "Failed to upsert deal offer")
    }
    return data.offer
  } catch (error) {
    console.error("Error upserting deal offer:", error)
    throw error
  }
}

export const updateDealCurrentStep = async (dealId: number, step: DealStep) => {
  try {
    const res = await axios.post(`/deals/step/update`, { step, dealId })
    const { data } = res
    if (!data.success) {
      throw new Error(data.message || "Failed to update deal current step")
    }
    return data.deal
  } catch (error) {
    console.error("Error updating deal current step:", error)
    throw error
  }
}

export const upsertDealClientInfo = async (dealId: number, data: Record<string, any>) => {
  try {
    const res = await axios.post(`/deals/${dealId}/client-info`, { data })
    const { data: resData } = res
    if (!resData.success) {
      throw new Error(resData.message || "Failed to update client info")
    }
    return resData.clientInfo
  } catch (error) {
    console.error("Error updating client info:", error)
    throw error
  }
}

export const upsertDealPunchlist = async (dealId: number, data: Record<string, any>) => {
  try {
    const res = await axios.post(`/deals/${dealId}/punchlist`, { data })
    const { data: resData } = res
    if (!resData.success) {
      throw new Error(resData.message || "Failed to update punchlist")
    }
    return resData.punchlist
  } catch (error) {
    console.error("Error updating punchlist:", error)
    throw error
  }
}

export const syncDealPunchlist = async (dealId: number) => {
  try {
    const res = await axios.post(`/deals/${dealId}/punchlist/sync`)
    const { data: resData } = res
    if (!resData.success) {
      throw new Error(resData.message || "Failed to sync punchlist")
    }
    return resData.punchlist
  } catch (error) {
    console.error("Error syncing punchlist:", error)
    throw error
  }
}

export const updateDealClient = async (dealId: number, data: Record<string, any>) => {
  try {
    const res = await axios.put(`/deals/${dealId}/client`, { data })
    const { data: resData } = res
    if (!resData.success) {
      throw new Error(resData.message || "Failed to update client")
    }
    return resData.client
  } catch (error) {
    console.error("Error updating client:", error)
    throw error
  }
}

export const updateDealGrant = async (grantId: number, data: Record<string, any>) => {
  try {
    const res = await axios.put(`/deals/grant/${grantId}`, { data })
    const { data: resData } = res
    if (!resData.success) {
      throw new Error(resData.message || "Failed to update grant")
    }
    return resData.grant
  } catch (error) {
    console.error("Error updating grant:", error)
    throw error
  }
}

export interface SmartSyncResult {
  deal: any
  stageUpdated: boolean
  previousStage: string | null
  newStage: string | null
  signals: string[]
}

export const smartSyncDeal = async (dealId: number): Promise<SmartSyncResult> => {
  try {
    const res = await axios.post(`/deals/${dealId}/smart-sync`)
    const { data } = res
    if (!data.success) throw new Error(data.message || "Failed to smart sync deal")
    return data
  } catch (error) {
    console.error("Error smart syncing deal:", error)
    throw error
  }
}

export const syncDealFromLegacy = async (dealId: number) => {
  try {
    const res = await axios.post(`/deals/${dealId}/sync-legacy`)
    const { data } = res
    if (!data.success) {
      throw new Error(data.message || "Failed to sync deal from legacy database")
    }
    return data.deal
  } catch (error) {
    console.error("Error syncing deal from legacy:", error)
    throw error
  }
}

export interface LegacyDealResult {
  dealIdStr: string
  clientFirstName: string
  clientLastName: string
  clientEmail: string
  companyName: string
  companyLegacyId: number
  stageName: string | null
  managerName: string | null
}

export const searchLegacyDeals = async (term: string): Promise<LegacyDealResult[]> => {
  const res = await axios.get(`/deals/legacy-search`, { params: { term } })
  return res.data.results
}

export const legacySyncByStr = async (dealIdStr: string): Promise<{ id: number; dealIdStr: string }> => {
  const res = await axios.post(`/deals/legacy-sync-by-str`, { dealIdStr })
  return res.data.deal
}

export const sendIntroEmail = async (dealId: number): Promise<{ success: boolean; clientName?: string }> => {
  const res = await axios.post(`/deals/${dealId}/send-intro-email`)
  return res.data
}

// ─── Deal Assessment Types ──────────────────────────────────────────────────

export type Tier = "A" | "B" | "C" | "D"

export interface DealScorecardSignal {
  key: string
  score: number | null
  rationale: string
  weight: number
}

export interface DealScorecardCategory {
  key: string
  label: string
  weight: number
  categoryScore: number
  signals: DealScorecardSignal[]
}

export interface DealAssessmentScores {
  overallScore: number
  tier: Tier
  gateFailed: boolean
  gateMessage: string | null
  categories: DealScorecardCategory[]
  keyStrengths: DealScorecardSignal[]
  keyRisks: DealScorecardSignal[]
  dataConfidence: "high" | "medium" | "low"
}

export interface DealAssessment {
  id: number
  dealId: number
  content: string
  scores: DealAssessmentScores
  overallScore: number | null
  tier: string | null
  dataConfidence: string | null
  dealMemo: string | null
  inputTokens: number | null
  outputTokens: number | null
  createdAt: string
}

export const DEAL_SIGNAL_LABELS: Record<string, string> = {
  pp_percentage: "PP Percentage",
  pp_split_balance: "PP Split Balance",
  lp_coverage: "LP Coverage",
  exercise_cost_reasonableness: "Exercise Cost Reasonableness",
  ltv_ratio: "LTV Ratio",
  client_employment_status: "Client Employment Status",
  vesting_completion: "Vesting Completion",
  grant_documentation_quality: "Grant Documentation Quality",
  time_to_close_estimate: "Time to Close",
  fund_concentration_headroom: "Fund Concentration Headroom",
  exposure_limit_status: "Exposure Limit Status",
  deal_size_appropriateness: "Deal Size Appropriateness",
  company_liquidity_outlook: "Company Liquidity Outlook",
  company_cap_table_health: "Company Cap Table Health",
  company_business_quality: "Company Business Quality",
}

export type DealAssessmentStreamEvent =
  | { type: "status"; text: string }
  | { type: "token"; text: string }
  | { type: "done"; assessment: DealAssessment }
  | { type: "error"; message: string }

export const getDealAssessment = async (dealId: number): Promise<{
  cached: boolean
  stale: boolean
  assessment: DealAssessment | null
  stalenessReason?: string
}> => {
  const res = await axios.get(`/deals/${dealId}/assessment`)
  return res.data
}

export const runDealAssessment = async (
  dealId: number,
  signal: AbortSignal | undefined,
  onEvent: (event: DealAssessmentStreamEvent) => void
): Promise<DealAssessment | null> => {
  const serverUrl = process.env.REACT_APP_SERVER_URL ?? ""
  const res = await fetch(`${serverUrl}/deals/${dealId}/assessment`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    signal,
  })
  if (!res.ok) throw new Error(`HTTP ${res.status}`)

  const reader = res.body!.getReader()
  const decoder = new TextDecoder()
  let buffer = ""
  let finalAssessment: DealAssessment | null = null

  while (true) {
    const { done, value } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })
    const lines = buffer.split("\n")
    buffer = lines.pop() ?? ""
    for (const line of lines) {
      if (!line.startsWith("data: ")) continue
      try {
        const event = JSON.parse(line.slice(6)) as DealAssessmentStreamEvent
        onEvent(event)
        if (event.type === "done") finalAssessment = event.assessment
      } catch { /* skip malformed lines */ }
    }
  }
  return finalAssessment
}

// ─── Deal Memo PDF Download ─────────────────────────────────────────────────

export const downloadDealMemo = async (dealId: number, dealName: string) => {
  const res = await axios.get(`/deals/${dealId}/memo-pdf`, {
    responseType: "blob",
  })
  const url = window.URL.createObjectURL(
    new Blob([res.data], { type: "application/pdf" })
  )
  const link = document.createElement("a")
  link.href = url
  link.setAttribute("download", `Deal Memo - ${dealName}.pdf`)
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(url)
}

export const generateContract = async (dealId: number, testMode: boolean = false) => {
  try {
    const res = await axios.post(`/contracts/generate`, { dealId, testMode })
    const { data } = res
    if (!data.success) {
      throw new Error(data.error || "Failed to generate contract")
    }
    return data as { success: boolean; files: { name: string; dropboxPath: string }[]; warnings: string[] }
  } catch (error: any) {
    console.error("Error generating contract:", error)
    throw error
  }
}

export const getPortalUrl = async (dealId: number): Promise<string> => {
  const res = await axios.get(`/deals/${dealId}/portal-url`)
  const { data } = res
  if (!data.success) {
    throw new Error(data.error || "Failed to generate portal URL")
  }
  return data.url
}
